import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useMutation, useQuery } from 'apollo-3';

import { get } from 'Components/Base/constants';
import { Flex, Box, ButtonText } from 'Components/Base';
import { userAssignmentDetail } from 'GraphQL/query/UserAssignment.query';
import Loading from 'Components/Loading';
import {
  submitUserAssignment,
  uploadUserAssignment,
  generateSignedUrl,
} from 'GraphQL/mutation/UserAssignment.mutation';
import { useCapacitor } from 'Util/Capacitors';

import AssignmentForm from '../../Components/AssignmentForm/AssignmentForm';
import AssignmentSubmissionModal, {
  SUBMISSION_VARIANT,
} from '../../Components/AssignmentSubmissionModal';
import LoadingModal from '../../Components/LoadingModal/LoadingModal';

const CardWrapper = styled((props) => <Box bg={'white'} {...props} />)`
  overflow: hidden;
  box-shadow: ${get('shadows.primary')};
  border-radius: 12px;
  border: 1px solid;
  border-color: ${({ theme }) => theme?.colors?.greyscale?.grey5 ?? '#f2f2f2'};
`;

const AssignmentSubmission = ({ content, classroomId, courseAssetId, onCompleted }) => {
  const { openUrl } = useCapacitor();
  const [dialogState, setDialogState] = useState();

  const [generateUrl, { loading }] = useMutation(generateSignedUrl, {
    onCompleted: (response) => {
      const signedUrl = response?.generateSignedUrl?.url;
      if (signedUrl) {
        return openUrl(signedUrl, {
          asBrowser: true,
          defaultFunction: async () => {
            const downloadButtonElement = document.createElement('a');

            let fileName =
              signedUrl?.split('/')?.pop?.()?.split?.('#')?.[0]?.split?.('?')?.[0] ?? '';
            if (fileName.includes('%')) {
              // File name is encoded twice.
              fileName = decodeURIComponent(decodeURIComponent(fileName));
            }
            const blob = await fetch(signedUrl).then((res) => res.blob());
            const objectURL = URL.createObjectURL(blob);
            downloadButtonElement.href = objectURL;
            downloadButtonElement.target = '_blank';
            // downloadButtonElement.download = fileName;
            downloadButtonElement.click();
            downloadButtonElement.remove();
          },
        });
      }
    },
    onError: (error) => {
      console.error(error);
      setDialogState(SUBMISSION_VARIANT.FILE_UNREACHABLE);
    },
  });

  const { loading: queryLoading, data: detail } = useQuery(userAssignmentDetail, {
    variables: {
      classroomId,
      courseAssetId,
    },
  });

  const [submitAssignment] = useMutation(submitUserAssignment, {
    onError: (error) => {
      if (error.message.includes('423')) {
        setDialogState(SUBMISSION_VARIANT.OVERDUE);
      } else {
        setDialogState(SUBMISSION_VARIANT.ERROR);
      }
    },
    onCompleted: () => {
      setDialogState(SUBMISSION_VARIANT.SUCCESS);
      onCompleted();
    },
    update: (caches, { data }) => {
      const submitUserAssignmentData = data?.submitUserAssignment;
      caches.writeQuery({
        query: userAssignmentDetail,
        variables: {
          classroomId: submitUserAssignmentData?.classroomId,
          courseAssetId: submitUserAssignmentData?.courseAssetRefId,
        },
        data: { userAssignmentDetail: submitUserAssignmentData },
      });
    },
  });

  const [uploadAssignment] = useMutation(uploadUserAssignment, {
    onError: () => setDialogState(SUBMISSION_VARIANT.ERROR),
  });

  const onSubmit = async (formData) => {
    setDialogState('loading');

    if (formData.type === 'FILE') {
      const uploadRes = await uploadAssignment({
        variables: {
          classroomId,
          courseAssetId,
          data: {
            filename: formData.attachment.name,
            contentType: formData.attachment.file.type,
          },
        },
      });
      const uploadUrl = uploadRes?.data?.uploadUserAssignment?.url;
      if (!uploadUrl) return;
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        body: formData.attachment.file,
        headers: {
          'Content-Disposition': `attachment; filename="${encodeURI(formData.attachment.name)}"`,
        },
      });
      if (!res.ok) {
        setDialogState(SUBMISSION_VARIANT.ERROR);
      } else {
        const data = {
          type: formData.type,
          content: {
            fileName: formData.attachment.name,
            key: uploadRes?.data?.uploadUserAssignment?.key,
            bucket: uploadRes?.data?.uploadUserAssignment?.bucket,
            provider: 'S3',
          },
          studentComment: formData.note,
        };
        await submitAssignment({
          variables: { classroomId, courseAssetId, data },
        });
      }
    } else {
      const data = {
        type: formData.type,
        content: {
          url: formData.url,
        },
        studentComment: formData.note,
      };
      await submitAssignment({
        variables: { classroomId, courseAssetId, data },
      });
    }
  };

  const fileDownLoadButton = useMemo(() => {
    const onDownloadFile = () =>
      generateUrl({
        variables: {
          id: detail?.userAssignmentDetail?.id,
        },
      });

    return (
      <ButtonText type="button" onClick={onDownloadFile} disabled={loading}>
        {loading ? (
          <Box width={20} height={20} m="auto">
            <Loading />
          </Box>
        ) : (
          'ดูไฟล์'
        )}
      </ButtonText>
    );
  }, [detail?.userAssignmentDetail?.id, generateUrl, loading]);

  if (queryLoading) {
    return <Loading />;
  }

  return (
    <>
      <CardWrapper>
        <Flex
          px={4}
          height={'44px'}
          justifyContent={'space-between'}
          alignItems={'center'}
          bg={'greyscale.grey5'}
        >
          Project Submission
        </Flex>
        <Box p={4} width={'100%'}>
          <AssignmentForm
            filePolicy={{
              fileTypes: content?.fileTypes ?? [],
              fileSizeLimit: content?.fileSizeLimit,
            }}
            status={
              detail?.userAssignmentDetail?.resultStatus ?? detail?.userAssignmentDetail?.status
            }
            type={content.type}
            dueDate={content?.dueDate ? new Date(content?.dueDate) : ''}
            submittedDate={new Date(detail?.userAssignmentDetail?.submittedAt)}
            reviewedDate={new Date(detail?.userAssignmentDetail?.reviewedAt)}
            url={detail?.userAssignmentDetail?.content?.url}
            attachment={detail?.userAssignmentDetail?.content?.fileName}
            note={
              detail?.userAssignmentDetail?.status === 'GRADED'
                ? detail?.userAssignmentDetail?.reviewedComment
                : detail?.userAssignmentDetail?.studentComment
            }
            fullScore={content?.fullScore}
            score={detail?.userAssignmentDetail?.score}
            onSubmit={(formData) => onSubmit(formData)}
            fileDownLoadButton={fileDownLoadButton}
          />
        </Box>
      </CardWrapper>
      <AssignmentSubmissionModal
        muiProps={{ isActive: dialogState && dialogState !== 'loading' }}
        variant={dialogState}
        onClose={() => setDialogState()}
      />
      <LoadingModal isOpen={dialogState === 'loading'} />
    </>
  );
};

AssignmentSubmission.propTypes = {
  content: PropTypes.object,
  classroomId: PropTypes.string,
  courseAssetId: PropTypes.string,
};

export default AssignmentSubmission;
